import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { authApi, formatApiError } from "@/lib/api";
import { Heart, Phone, Lock, IdentificationCard, ShieldCheck, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function LoginPage() {
  const { login, verifyLoginOtp } = useAuth();
  const [step, setStep] = useState("credentials"); // 'credentials' | 'otp'
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [ukc, setUkc] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // OTP step state
  const [pendingToken, setPendingToken] = useState("");
  const [otpChannel, setOtpChannel] = useState("");
  const [otpContact, setOtpContact] = useState("");
  const [otp, setOtp] = useState("");
  const [trustDevice, setTrustDevice] = useState(true);
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownRef = useRef(null);

  useEffect(() => () => { if (cooldownRef.current) clearInterval(cooldownRef.current); }, []);

  const startCooldown = (seconds = 30) => {
    setResendCooldown(seconds);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setResendCooldown((s) => {
        if (s <= 1) { clearInterval(cooldownRef.current); cooldownRef.current = null; return 0; }
        return s - 1;
      });
    }, 1000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const result = await login(phone.trim(), password, ukc.trim().toUpperCase());
      if (result?.requiresOtp) {
        setPendingToken(result.pendingToken);
        setOtpChannel(result.channel);
        setOtpContact(result.contactMasked || "");
        setStep("otp");
        startCooldown(30);
        toast.info(`Code sent via ${result.channel.toUpperCase()} to ${result.contactMasked}`);
      } else {
        toast.success("Welcome back!");
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await verifyLoginOtp(pendingToken, otp.trim(), trustDevice);
      toast.success("Welcome back!");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async (channel) => {
    if (resendCooldown > 0) return;
    setError("");
    try {
      const { data } = await authApi.resendOtp(pendingToken, channel);
      setOtpChannel(data.channel);
      toast.success(`New code sent via ${data.channel.toUpperCase()}`);
      startCooldown(30);
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  // const handleGoogleLogin = () => {
  //   // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  //   const redirectUrl = window.location.origin + "/dashboard";
  //   window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  // };

  const handleBackToCredentials = () => {
    setStep("credentials");
    setOtp("");
    setError("");
    setPendingToken("");
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4" data-testid="login-page">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2.5 mb-6">
            <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
              <Heart className="w-5 h-5 text-primary-foreground" weight="fill" />
            </div>
            <span className="font-heading text-2xl font-bold tracking-tighter">Utheri</span>
          </Link>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">
            {step === "credentials" ? "Welcome back" : "Verify it's you"}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {step === "credentials" ? "Sign in with your phone number" : `We sent a 6-digit code to ${otpContact || "your contact"}`}
          </p>
        </div>

        <div className="bg-card rounded-xl border border-border p-6 shadow-sm space-y-4">
          {error && (
            <div className="bg-destructive/10 border border-destructive/20 text-destructive text-sm rounded-lg p-3" data-testid="login-error">
              {error}
            </div>
          )}

          {step === "credentials" ? (
            <>
              {/* <button
                onClick={handleGoogleLogin}
                data-testid="google-login-btn"
                className="w-full flex items-center justify-center gap-2.5 py-2.5 rounded-xl border border-input bg-card text-foreground font-medium text-sm hover:bg-secondary transition-colors active:scale-95"
              >
                <GoogleLogo className="w-5 h-5" weight="bold" />
                Sign in with Google
              </button>

              <div className="relative"> */}

              {/* <div className="relative">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
                <div className="relative flex justify-center text-xs"><span className="bg-card px-3 text-muted-foreground">or continue with phone</span></div>
              </div> */}

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-credentials-form">
                <div>
                  <label className="text-sm font-medium text-foreground block mb-1.5">Phone or Member Number</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input
                      data-testid="login-phone-input"
                      type="text"
                      placeholder="07XX XXX XXX  or  132"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="w-full pl-10 pr-3 py-2.5 text-sm bg-card border border-input rounded-xl focus:outline-none focus:ring-2 focus:ring-ring"
                      autoComplete="username"
                      required
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Type your phone, or your member number (e.g. <strong>132</strong> — we'll add the UKC- prefix).</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground block mb-1.5">
                    Member Number <span className="text-muted-foreground font-normal">(optional cross-check)</span>
                  </label>
                  <div className="relative">
                    <IdentificationCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input
                      data-testid="login-ukc-input"
                      type="text"
                      placeholder="132 or UKC-0132"
                      value={ukc}
                      onChange={(e) => setUkc(e.target.value)}
                      className="w-full pl-10 pr-3 py-2.5 text-sm bg-card border border-input rounded-xl focus:outline-none focus:ring-2 focus:ring-ring uppercase"
                      autoComplete="off"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Extra check that this UKC matches the account. Skip if you used your member number above.</p>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-sm font-medium text-foreground">Password</label>
                    <Link to="/forgot-password" className="text-xs text-primary hover:underline" data-testid="forgot-password-link">
                      Forgot password?
                    </Link>
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input
                      data-testid="login-password-input"
                      type="password"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-10 pr-3 py-2.5 text-sm bg-card border border-input rounded-xl focus:outline-none focus:ring-2 focus:ring-ring"
                      autoComplete="current-password"
                      required
                    />
                  </div>
                </div>
                <button
                  data-testid="login-submit-btn"
                  type="submit"
                  disabled={loading}
                  className="w-full py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold text-sm active:scale-95 transition-transform disabled:opacity-60"
                >
                  {loading ? "Signing in..." : "Sign In"}
                </button>
              </form>
            </>
          ) : (
            <form onSubmit={handleVerifyOtp} className="space-y-4" data-testid="login-otp-form">
              <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 flex items-center gap-2.5">
                <ShieldCheck className="w-5 h-5 text-primary shrink-0" weight="duotone" />
                <p className="text-xs text-foreground">Code sent via <strong>{otpChannel?.toUpperCase()}</strong> to <strong>{otpContact}</strong></p>
              </div>
              <div>
                <label className="text-sm font-medium text-foreground block mb-1.5">6-digit code</label>
                <input
                  data-testid="login-otp-input"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="• • • • • •"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="w-full text-center text-2xl tracking-[0.5em] font-mono py-3 bg-card border border-input rounded-xl focus:outline-none focus:ring-2 focus:ring-ring"
                  autoFocus
                  required
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                <input
                  type="checkbox"
                  checked={trustDevice}
                  onChange={(e) => setTrustDevice(e.target.checked)}
                  data-testid="login-trust-device-checkbox"
                  className="w-4 h-4 rounded border-input text-primary focus:ring-ring"
                />
                Trust this device for 30 days
              </label>
              <button
                data-testid="login-verify-otp-btn"
                type="submit"
                disabled={loading || otp.length !== 6}
                className="w-full py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold text-sm active:scale-95 transition-transform disabled:opacity-60"
              >
                {loading ? "Verifying..." : "Verify & sign in"}
              </button>
              <div className="flex items-center justify-between text-xs">
                <button
                  type="button"
                  onClick={handleBackToCredentials}
                  data-testid="login-otp-back-btn"
                  className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
                >
                  <ArrowLeft className="w-3.5 h-3.5" /> Back
                </button>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => handleResend()}
                    disabled={resendCooldown > 0}
                    data-testid="login-otp-resend-btn"
                    className="text-primary disabled:text-muted-foreground hover:underline disabled:no-underline"
                  >
                    {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleResend(otpChannel === "sms" ? "email" : "sms")}
                    disabled={resendCooldown > 0}
                    data-testid="login-otp-switch-channel-btn"
                    className="text-primary disabled:text-muted-foreground hover:underline disabled:no-underline"
                  >
                    Try {otpChannel === "sms" ? "email" : "SMS"} instead
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>

        {/* {step === "credentials" && (
          <p className="text-center text-sm text-muted-foreground mt-4">
            Don't have an account?{" "}
            <Link to="/register" className="text-primary font-medium underline" data-testid="go-to-register">
              Register
            </Link>
          </p>
        )} */}
      </div>
    </div>
  );
}
